import {useCurrentUser} from 'hooks/useCurrentUser';
import {Cart} from 'lib/Cart';
import {normalize} from 'lib/normalize';
import {rootElement} from 'modules/Cart';
import {useCartAuthContext} from 'modules/Cart/contexts/cartAuthContext';
import {
    CartContextTotal,
    CartContextType,
    CartProductData,
    CartProductSource,
    CartSourceData,
    CartStep,
} from 'modules/Cart/contexts/cartContext';
import {useCallback, useEffect, useMemo, useReducer, useState} from 'react';

type State = Pick<CartContextType, 'products' | 'step' | 'productsIds'> & {
    isReady: boolean;
};

type Action =
    | {type: 'setStep'; payload: CartStep}
    | {type: 'setProductCount'; payload: {id: number; count: number}}
    | {type: 'setDeleted'; payload: {id: number; deleted: boolean}}
    | {type: 'setChecked'; payload: {id: number; checked: boolean}}
    | {type: 'setAllChecked'; payload: boolean}
    | {type: 'addProduct'; payload: CartProductData}
    | {type: 'setInitialState'; payload: CartProductData[]};

const initialState: State = {
    step: CartStep.Products,
    products: {},
    productsIds: [],
    isReady: false,
};

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'setStep': {
            return {
                ...state,
                step: action.payload,
            };
        }
        case 'setProductCount': {
            const {id, count} = action.payload;
            const targetProduct = state.products[id];
            if (!targetProduct) {
                return state;
            }

            return {
                ...state,
                products: {
                    ...state.products,
                    [id]: {
                        ...targetProduct,
                        count,
                    },
                },
            };
        }
        case 'setDeleted': {
            const {id, deleted} = action.payload;
            const targetProduct = state.products[id];
            if (!targetProduct) {
                return state;
            }

            return {
                ...state,
                products: {
                    ...state.products,
                    [id]: {
                        ...targetProduct,
                        deleted,
                    },
                },
            };
        }
        case 'setChecked': {
            const {id, checked} = action.payload;
            const targetProduct = state.products[id];
            if (!targetProduct) {
                return state;
            }

            return {
                ...state,
                products: {
                    ...state.products,
                    [id]: {
                        ...targetProduct,
                        checked,
                    },
                },
            };
        }
        case 'setAllChecked': {
            const newProducts = Object.values(state.products).reduce(
                (acc, product) => {
                    acc[product.id] = {
                        ...product,
                        checked: action.payload,
                    };
                    return acc;
                },
                {} as CartContextType['products'],
            );

            return {
                ...state,
                products: newProducts,
            };
        }
        case 'addProduct': {
            const product = action.payload;
            const targetProduct = state.products[product.id];

            // если такой товар уже есть в корзине
            if (targetProduct) {
                return {
                    ...state,
                    products: {
                        ...state.products,
                        [targetProduct.id]: {
                            ...targetProduct,
                            count: targetProduct.count + product.count,
                        },
                    },
                };
            }

            return {
                ...state,
                products: {
                    ...state.products,
                    [product.id]: product,
                },
                productsIds: [...state.productsIds, product.id],
            };
        }
        case 'setInitialState': {
            const products = action.payload;

            return {
                ...state,
                products: normalize(products),
                productsIds: products.map((product) => product.id),
                isReady: true,
            };
        }
    }
};

export function useCartState(): CartContextType {
    const [{step, products, productsIds, isReady}, dispatch] = useReducer(
        reducer,
        initialState,
    );

    const regionalDiscounts = window.ENV?.regionalSettings?.discount || 1;

    const {withAuth} = useCartAuthContext();
    const {isLoading: currentUserIsLoading} = useCurrentUser({
        disabled: !withAuth,
    });
    const shouldApplyRegionalDiscount = currentUserIsLoading ? false : isReady;

    useEffect(() => {
        function getSourceCartData() {
            try {
                return JSON.parse(rootElement?.getAttribute('data-cart') || '');
            } catch (error) {
                return {};
            }
        }

        const sourceCartData = getSourceCartData();
        const initialProducts = extendSourceDataProducts(
            parseCartSourceData(sourceCartData),
        );

        dispatch({type: 'setInitialState', payload: initialProducts});
    }, []);

    const setStep = useCallback((step: CartStep) => {
        dispatch({type: 'setStep', payload: step});
    }, []);

    const setProductCount = useCallback(
        (id: number, count: number) => {
            const targetProduct = products[id];
            const newCount = count;
            const oldCount = targetProduct.count;

            Cart.update({
                id: targetProduct.id,
                price: targetProduct.price,
                count: newCount - oldCount,
                discount: targetProduct.discount,
            });

            dispatch({type: 'setProductCount', payload: {id, count}});
        },
        [products],
    );

    const setDeleted = useCallback(
        (id: number, deleted: boolean) => {
            const targetProduct = products[id];

            if (deleted) {
                Cart.remove(id);
            } else {
                Cart.update({
                    id: targetProduct.id,
                    price: targetProduct.price,
                    count: targetProduct.count,
                    discount: targetProduct.discount,
                });
            }
            dispatch({type: 'setDeleted', payload: {id, deleted}});
        },
        [products],
    );

    const setChecked = useCallback((id: number, checked: boolean) => {
        dispatch({type: 'setChecked', payload: {id, checked}});
    }, []);

    const setAllChecked = useCallback((checked: boolean) => {
        dispatch({type: 'setAllChecked', payload: checked});
    }, []);

    const addProduct = useCallback((product: CartProductData) => {
        dispatch({type: 'addProduct', payload: product});
    }, []);

    const total = useMemo(() => {
        const list = Object.values(products).filter(
            (product) => !product.deleted && product.checked,
        );

        const result: CartContextTotal = list.reduce(
            (acc, product) => {
                // стоимость позиции по конкретному товару(учитывается кол-во товаров)
                const positionPriceCurrent =
                    product.count * product.price.current;
                // старая стоимость позиции по конкретному товару(учитывается кол-во товаров)
                const positionPriceOld = product.count * product.price.old;
                // скидка на позицию по конкретному товару(учитывается кол-во товаров)
                const positionDiscount =
                    positionPriceOld - positionPriceCurrent;

                acc.price += positionPriceOld;
                acc.productsAmount += product.count;
                acc.productsTypeAmount += 1;
                acc.discount += positionDiscount;

                const positionHasRegionalDiscount = Boolean(
                    shouldApplyRegionalDiscount && product.discount,
                );

                if (positionHasRegionalDiscount) {
                    acc.cartHasRegionalDiscount = true;
                }

                // добавляем итоговую стоимость позиции к общей стоимости корзины
                // текущая стоимость позиции умножается на коэффициент скидки
                acc.totalPrice +=
                    positionPriceCurrent *
                    (positionHasRegionalDiscount ? regionalDiscounts : 1);

                return acc;
            },
            {
                price: 0,
                productsAmount: 0,
                productsTypeAmount: 0,
                totalPrice: 0,
                discount: 0,
                cartHasRegionalDiscount: false,
            } as CartContextTotal,
        );

        result.totalPrice = Math.ceil(result.totalPrice);

        return result;
    }, [products, regionalDiscounts, shouldApplyRegionalDiscount]);

    const [orderFormIsValid, setOrderFormIsValid] = useState(false);
    const [formSubmitCount, setSubmitCountForm] = useState(0);
    const [orderFormIsSubmitting, setOrderFormIsSubmitting] = useState(false);

    return {
        products,
        productsIds,
        step,
        total,
        setStep,
        setProductCount,
        setDeleted,
        setChecked,
        setAllChecked,
        orderFormIsValid,
        setOrderFormIsValid,
        orderFormIsSubmitting,
        setOrderFormIsSubmitting,
        addProduct,
        regionalDiscounts,
        shouldApplyRegionalDiscount,
        isReady,
        setSubmitCountForm,
        formSubmitCount,
    };
}

// расширяем данные о товарах клиентскими полями
function extendSourceDataProducts(
    products: CartProductSource[],
): CartProductData[] {
    return products.map((product) => {
        return {
            ...product,
            checked: true,
        };
    });
}

function parseCartSourceData(cartData: CartSourceData): CartProductSource[] {
    let products: CartProductSource[] = [];

    if (cartData.goods) {
        products = products.concat(cartData.goods);
    }

    // if (cartData.complects) {
    //     cartData.complects.forEach((complect) => {
    //         products = products.concat(complect.goods);
    //     });
    // }

    return products;
}
